import { useInfiniteQuery } from '@tanstack/react-query'
import { fetchApiWithToken, type WithHydraPagination } from '~/api/utils'
import type { OfferResponse } from '../types'

export type OffersLDResponse = WithHydraPagination<OfferResponse>

export const useOffersAutocomplete = ({
  userIds,
  ids,
  search,
  agencyId,
  additionalFields
}: {
  userIds: number[]
  ids?: number[]
  search?: string
  agencyId?: number
  additionalFields?: string[]
}) => {
  return useInfiniteQuery({
    queryKey: [
      'offersAutocomplete',
      userIds,
      ids,
      search,
      agencyId,
      additionalFields
    ],
    queryFn: async ({ pageParam = 1 }): Promise<OffersLDResponse> => {
      if (!userIds || userIds.length === 0) {
        return {
          '@context': '/contexts/Offer',
          '@id': '/v1/offers',
          '@type': 'hydra:Collection',
          'hydra:member': [],
          'hydra:totalItems': 0
        }
      }

      const searchParams = new URLSearchParams()
      searchParams.append('page', String(pageParam))
      searchParams.append('itemsPerPage', '30')
      searchParams.append('user', userIds.join(','))
      searchParams.append('archived', 'false')
      searchParams.append('order[id]', 'desc')

      if (search) {
        searchParams.append('q', search)
      }

      if (ids && ids.length > 0) {
        searchParams.append('id[]', ids.join(','))
      }

      if (agencyId) {
        searchParams.append('agency', String(agencyId))
      }

      const baseFields = ['id', 'title', 'reference', 'companyName', 'order']
      const fieldsParam = [...baseFields, ...(additionalFields ?? [])]
        .map((field) => `fields[]=${field}`)
        .join('&')

      const data = await fetchApiWithToken<OffersLDResponse>({
        method: 'get',
        url: `/v1/offers?${searchParams.toString()}&${fieldsParam}`,
        useLDJson: true
      })

      return data
    },
    getNextPageParam: (lastPage, allPages) => {
      const totalItems = lastPage['hydra:totalItems'] ?? 0
      const loadedItems = allPages.reduce(
        (sum, page) => sum + (page['hydra:member']?.length ?? 0),
        0
      )

      if (loadedItems < totalItems) {
        return allPages.length + 1
      }
      return undefined
    },
    initialPageParam: 1,
    enabled: userIds.length > 0
  })
}
